import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';
import { usePublishedPages } from '@/hooks/usePublishedPages';
import { cn } from '@/lib/utils';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { config } = useTenant();
  const { t, isMultiLanguageEnabled } = useLanguage();
  const { pages: publishedPages, loading: pagesLoading } = usePublishedPages();

  /**
   * Build navigation from published pages + core functional pages
   * Core pages (ventas, arriendos) are always shown as they are functional pages
   */
  const navigation = useMemo(() => {
    // Return empty array if config is not loaded yet
    if (!config) return [];

    const navItems = [];

    // Always include HOME if exists
    const homePage = publishedPages.find(p => p.slug === 'home' || p.type === 'HOME');
    if (homePage) {
      navItems.push({
        name: t('nav.home'),
        href: '/'
      });
    }

    // Always include functional pages (properties)
    navItems.push(
      { name: t('nav.sales'), href: '/ventas' },
      { name: t('nav.rentals'), href: '/arriendos' }
    );

    // Add dynamic pages from CMS (excluding home, ventas, arriendos which are handled separately)
    const dynamicPages = publishedPages.filter(page => {
      const slug = page.slug.toLowerCase();
      // Exclude pages that are handled as core/functional pages
      return !['home', 'ventas', 'arriendos'].includes(slug);
    });

    // Add dynamic pages
    dynamicPages.forEach(page => {
      navItems.push({
        name: page.name,
        href: page.href
      });
    });

    // Always include blog if enabled in config
    if (config.features.blog) {
      navItems.push({ name: t('nav.blog'), href: '/blog' });
    }

    return navItems;
  }, [publishedPages, config, t]);

  // Don't render header if config is not loaded yet
  if (!config) return null;

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      {/* Top Bar */}
      <div className="hidden md:flex bg-gray-50 py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Phone className="h-4 w-4" />
              <span>{config.contact.phone}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Mail className="h-4 w-4" />
              <span>{config.contact.email}</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Solo mostrar selector de idiomas si está habilitado para el tenant */}
            {isMultiLanguageEnabled && (
              <LanguageSwitcher
                variant="compact"
                showFlags={true}
                className="hidden sm:flex"
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="flex items-center">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: config.primaryColor }}
              >
                {config.name.charAt(0)}
              </div>
              <span className="ml-2 text-xl font-bold text-gray-900">
                {config.name}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {pagesLoading ? (
              <div className="text-gray-400 text-sm">{t('header.loadingMenu')}</div>
            ) : (
              navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
                  style={{
                    '--hover-color': config.primaryColor
                  } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = config.primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '';
                  }}
                >
                  {item.name}
                </Link>
              ))
            )}
          </nav>

          {/* Mobile menu button and language switcher */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Language switcher visible on mobile */}
            {isMultiLanguageEnabled && (
              <LanguageSwitcher
                variant="compact"
                showFlags={true}
                className="flex"
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col space-y-2">
              {pagesLoading ? (
                <div className="text-gray-400 text-sm px-3 py-2">{t('header.loadingMenu')}</div>
              ) : (
                navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-gray-700 hover:text-gray-900 px-3 py-2 text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
